import { useCallback, useEffect, useState } from 'react'

import { DatePicker } from '../../lib/DatePicker'
import { todayThai } from '../../lib/datetime'
import { errorText } from '../../lib/offsite'
import { Pager } from '../../lib/Pager'
import { useSession } from '../../lib/SessionContext'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { SectionHead } from '../offsite/parts'

/* ============================================================================
   เวลาเข้า–ออกทั้งบริษัทรายวัน + แก้ว่ารอยสแกนไหนคือเข้า/ออก
   API: backend/src/routes/adminAttendance.js
        lib/attendanceDay.js (รายวัน) · lib/taFix.js (ติดป้ายรอยสแกน)

   ── ต่างจาก "ข้อมูลบุคคล" ยังไง ─────────────────────────────────────────────
   หน้านั้นดูทีละคนหลายวัน · หน้านี้ดูหลายคนในวันเดียว
   คำถามที่ตอบได้เฉพาะมุมนี้คือ "วันนี้ใครยังไม่มา ใครสแกนไม่ครบ"
   ซึ่งต้องรู้ก่อนสิ้นวัน ไม่ใช่มารู้ตอนสรุปเดือน

   ── ⚠️ การติดป้ายมีผลจริงกับเงินเดือน ──────────────────────────────────────
   HR_TA_DATA เก็บแค่ "มีคนแตะบัตร" ไม่ได้บอกว่าเข้าหรือออก เครื่องคำนวณจึงเดาว่า
   เร็วสุด = เข้า · ช้าสุด = ออก ซึ่งผิดเวลาคนแตะหลายครั้ง (ของจริงเจอ 17 รอย/วัน)
   ติดป้าย 'I'/'O' แล้ว usp_RecalcAttendance จะใช้รอยนั้นแทน แล้วคิดสาย/ขาดใหม่ทันที
   ============================================================================ */

type BranchOption = { BranchCode: string; BranchName: string; inactive: boolean }

type DayRow = {
  empCode: string
  empName: string
  departCode: string | null
  branchCode: string | null
  branchName: string | null
  shiftName: string | null
  shiftIn: string | null
  shiftOut: string | null
  inAt: string | null
  outAt: string | null
  lateMinute: number
  remark: string | null
  isHoliday: boolean
  hasPlan: boolean
  abnormal: boolean
}

type DayResult = {
  date: string
  items: DayRow[]
  total: number
  page: number
  pageSize: number
  totals: {
    people: number
    present: number
    missing: number
    late: number
    holiday: number
    abnormal: number
  }
}

type Scan = { seq: number; workDate: string; taTime: string; tag: string }

type ScanDay = {
  workDate: string
  plainCount: number
  firstPunch: string | null
  lastPunch: string | null
  designatedIn: string | null
  designatedOut: string | null
  effIn: string | null
  effOut: string | null
  workDayRemark: string | null
  isHoliday: boolean
}

type ScanResult = {
  employee: { empId: string; empCode: string; card: string; empName: string }
  range: { from: string; to: string }
  scans: Scan[]
  days: ScanDay[]
}

const BASE = '/api/admin/attendance'
const nf = new Intl.NumberFormat('th-TH')

/** 'YYYY-MM-DD HH:mm:ss' → 'HH:mm' */
const clock = (s: string | null) => (s ? s.slice(11, 16) : '-')

export default function AttendanceDay() {
  const { call, state } = useSession()
  const canFix =
    state.status === 'signed-in' &&
    state.session.kind === 'admin' &&
    ['superadmin', 'admin', 'hr'].includes(state.session.role)

  const [date, setDate] = useState(() => todayThai())
  const [branchCode, setBranchCode] = useState('')
  const [abnormalOnly, setAbnormalOnly] = useState(false)
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const [branches, setBranches] = useState<BranchOption[]>([])
  useEffect(() => {
    call<{ rows: BranchOption[] }>('/api/admin/branches?pageSize=200')
      .then((r) => setBranches(r.rows))
      .catch(() => {})
  }, [call])
  const sort = useSort([{ key: 'empCode', dir: 'asc' }], () => setPage(1))

  const [data, setData] = useState<DayResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* แผงแก้รอยสแกน — เปิดทีละคน ไม่ใช่ทั้งตาราง เพราะเป็นงานที่ต้องดูทีละเคส */
  const [fixing, setFixing] = useState<DayRow | null>(null)
  const [scans, setScans] = useState<ScanResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [fixError, setFixError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({
        date,
        page: String(page),
        pageSize: String(pageSize),
      })
      if (q) qs.set('q', q)
      if (branchCode) qs.set('branchCode', branchCode)
      if (abnormalOnly) qs.set('abnormal', '1')
      if (sort.param) qs.set('sort', sort.param)
      setData(await call<DayResult>(`${BASE}/day?${qs}`))
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [call, date, page, pageSize, q, branchCode, abnormalOnly, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  const openFix = async (row: DayRow) => {
    setFixing(row)
    setScans(null)
    setFixError(null)
    setNotice(null)
    setBusy(true)
    try {
      const qs = new URLSearchParams({ empCode: row.empCode, from: date, to: date })
      setScans(await call<ScanResult>(`${BASE}/scans?${qs}`))
    } catch (err) {
      setFixError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const tag = async (seq: number, value: 'I' | 'O' | '') => {
    if (!fixing) return
    setBusy(true)
    setFixError(null)
    try {
      const r = await call<ScanResult & { updated: number }>(`${BASE}/scans/tag`, {
        method: 'POST',
        body: JSON.stringify({ empCode: fixing.empCode, tags: [{ seq, value }] }),
      })
      setScans(r)
      setNotice('บันทึกและคิดเวลาใหม่แล้ว')
      // ตารางหลักต้องเห็นผลด้วย เพราะ remark/สาย เปลี่ยนไปแล้ว
      await load()
    } catch (err) {
      setFixError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const rows = data?.items ?? []

  return (
    <>
      <h1>เวลาเข้า–ออก รายวัน</h1>
      <p className="meta">
        ทั้งบริษัทในวันเดียว — ใช้ตอบว่า<strong>วันนี้ใครยังไม่มา ใครสแกนไม่ครบ</strong>{' '}
        ต่างจากหน้า “ข้อมูลบุคคล” ที่ดูทีละคนหลายวัน
      </p>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      <section className="card">
        <SectionHead
          title={`เวลาเข้า–ออก วันที่ ${date}`}
          meta={data ? `${nf.format(data.total)} คน` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(term.trim())
          }}
        >
          <label>
            วันที่
            <DatePicker
              value={date}
              onChange={(v) => {
                setPage(1)
                setDate(v)
                setFixing(null)
              }}
            />
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <label>
            สาขา
            <select
              value={branchCode}
              onChange={(e) => {
                setPage(1)
                setBranchCode(e.target.value)
              }}
            >
              <option value="">ทุกสาขา</option>
              {branches
                .filter((b) => !b.inactive)
                .map((b) => (
                  <option key={b.BranchCode} value={b.BranchCode}>
                    {b.BranchName || b.BranchCode}
                  </option>
                ))}
            </select>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={abnormalOnly}
              onChange={(e) => {
                setPage(1)
                setAbnormalOnly(e.target.checked)
              }}
            />
            เฉพาะที่ผิดปกติ
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
        </form>

        {data && (
          <p className="meta">
            ทั้งหมด <strong>{nf.format(data.totals.people)}</strong> คน · มาแล้ว{' '}
            {nf.format(data.totals.present)} · ยังไม่ครบคู่{' '}
            <strong className={data.totals.missing > 0 ? 'bad' : undefined}>
              {nf.format(data.totals.missing)}
            </strong>{' '}
            · สาย {nf.format(data.totals.late)} · วันหยุดของคน{' '}
            {nf.format(data.totals.holiday)} · ผิดปกติรวม {nf.format(data.totals.abnormal)}
          </p>
        )}

        <SortBar
          sort={sort.sort}
          labels={{ empCode: 'รหัส', empName: 'ชื่อ', depart: 'แผนก', branch: 'สาขา', late: 'สาย' }}
          onClear={() => sort.setSort([])}
        />

        {data === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : rows.length === 0 ? (
          <p className="meta">
            {abnormalOnly ? 'ไม่มีใครผิดปกติในวันนี้' : 'ไม่มีข้อมูลของวันนี้'}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <SortTh label="รหัส" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <SortTh label="ชื่อ" sortKey="empName" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="แผนก" sortKey="depart" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <SortTh label="สาขา" sortKey="branch" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <th className="nowrap">กะ</th>
                  <th>เข้า</th>
                  <th>ออก</th>
                  <SortTh label="สาย" sortKey="late" sort={sort.sort} onToggle={sort.toggle} />
                  <th>หมายเหตุ</th>
                  {canFix && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.empCode} className={r.isHoliday ? 'off' : undefined}>
                    <td className="nowrap">{r.empCode}</td>
                    <td>{r.empName}</td>
                    <td className="nowrap meta">{r.departCode ?? '-'}</td>
                    <td className="nowrap meta">{r.branchName ?? r.branchCode ?? '-'}</td>
                    <td className="nowrap meta">
                      {r.shiftIn && r.shiftOut ? `${r.shiftIn}-${r.shiftOut}` : '-'}
                    </td>
                    <td className={!r.inAt && r.hasPlan && !r.isHoliday ? 'bad' : undefined}>
                      {r.inAt ?? '-'}
                    </td>
                    <td className={!r.outAt && r.hasPlan && !r.isHoliday ? 'bad' : undefined}>
                      {r.outAt ?? '-'}
                    </td>
                    <td>{r.lateMinute > 0 ? <strong className="bad">{r.lateMinute}</strong> : '-'}</td>
                    <td className="meta">{r.remark ?? '-'}</td>
                    {canFix && (
                      <td className="nowrap">
                        <button
                          type="button"
                          className="ghost tiny"
                          disabled={busy}
                          onClick={() => void openFix(r)}
                        >
                          แก้รอยสแกน
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data !== null && data.total > 0 && (
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            loading={loading}
            onPage={setPage}
            onPageSize={(n) => {
              setPage(1)
              setPageSize(n)
            }}
          />
        )}
      </section>

      {/* แผงแก้รอยสแกน — เปิดเมื่อกดจากแถวใดแถวหนึ่ง */}
      {canFix && fixing && (
        <section className="card">
          <SectionHead
            title={`รอยสแกนของ ${fixing.empCode} ${fixing.empName}`}
            meta={scans ? `บัตร ${scans.employee.card} · ${scans.scans.length} รอย` : undefined}
            onReload={() => void openFix(fixing)}
            loading={busy}
          />

          <p className="meta">
            เครื่องคำนวณเดาเองว่า <strong>รอยเร็วสุด = เข้า · ช้าสุด = ออก</strong> —
            ถ้าเดาผิด ให้ระบุเองว่ารอยไหนคือเข้าหรือออก แล้วระบบจะคิดสาย/ขาดใหม่ทันที
          </p>

          {fixError && <p className="error">{fixError}</p>}
          {notice && <p className="ok">{notice}</p>}

          {scans === null ? (
            <p className="meta">กำลังโหลด…</p>
          ) : scans.scans.length === 0 ? (
            <p className="meta">วันนี้ไม่มีรอยสแกนของคนนี้เลย</p>
          ) : (
            <>
              {scans.days.map((d) => (
                <p className="meta" key={d.workDate}>
                  ผลที่เครื่องคำนวณใช้: เข้า <strong>{clock(d.effIn)}</strong> · ออก{' '}
                  <strong>{clock(d.effOut)}</strong>
                  {d.designatedIn && ' (เข้าถูกระบุเอง)'}
                  {d.designatedOut && ' (ออกถูกระบุเอง)'}
                  {' · รอยที่ไม่ได้ระบุ '}
                  {d.plainCount} รอย
                </p>
              ))}

              <div className="table-wrap table-wrap--rows10">
                <table className="data">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th>สถานะ</th>
                      <th>ระบุเป็น</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scans.scans.map((s) => (
                      <tr key={s.seq}>
                        <td className="nowrap">
                          <strong>{s.taTime.slice(11, 16)}</strong>
                        </td>
                        <td className="nowrap">
                          {s.tag === 'I' && <span className="ok">เวลาเข้า</span>}
                          {s.tag === 'O' && <span className="ok">เวลาออก</span>}
                          {s.tag === '' && <span className="meta">รอยปกติ</span>}
                          {s.tag !== '' && s.tag !== 'I' && s.tag !== 'O' && (
                            <span className="meta">ตัดออก ({s.tag})</span>
                          )}
                        </td>
                        <td className="nowrap">
                          <button
                            type="button"
                            className="ghost tiny"
                            disabled={busy || s.tag === 'I'}
                            onClick={() => void tag(s.seq, 'I')}
                          >
                            เข้า
                          </button>{' '}
                          <button
                            type="button"
                            className="ghost tiny"
                            disabled={busy || s.tag === 'O'}
                            onClick={() => void tag(s.seq, 'O')}
                          >
                            ออก
                          </button>{' '}
                          <button
                            type="button"
                            className="ghost tiny"
                            disabled={busy || s.tag === ''}
                            onClick={() => void tag(s.seq, '')}
                          >
                            ล้าง
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <p>
            <button type="button" className="ghost" onClick={() => setFixing(null)}>
              ปิด
            </button>
          </p>
        </section>
      )}
    </>
  )
}
